<template>
  <div class="bubble-chart">
    <div class="small-screen-message">
      <div class="small-screen-inner">
        <div class="screen-icon">🖥️</div>
        <p class="msg-it">Utilizza un dispositivo con uno schermo più grande per visualizzare il grafico interattivo.</p>
        <p class="msg-en">Please use a device with a larger screen to view the interactive chart.</p>
      </div>
    </div>

    <h1>Persone menzionate nei documenti di Aldo Moro — rete di co-menzioni</h1>
    <p class="subtitle">Clicca su un nodo per esplorare le sue connessioni</p>
    
    <div class="chart-container">
      <div class="controls">
        <button 
          id="resetBtn" 
          :class="{ hidden: !focusedNode }"
          @click="resetView"
        >
          ← Torna alla vista completa
        </button>
      </div>
      <div ref="chart"></div>
    </div>

    <div 
      id="tooltip" 
      class="tooltip"
      :style="tooltipStyle"
    ></div>

    <div 
      id="overlay"
      :style="{ display: showOverlay ? 'flex' : 'none' }"
    >
      <h2 id="overlay-title">{{ overlayTitle }}</h2>
      <p id="overlay-sub">{{ overlaySub }}</p>
      <ul id="overlay-list">
        <li v-for="doc in overlayDocs" :key="doc.id">
          <a 
            :href="`https://aldomorodigitale.unibo.it/edition/browse/work/${doc.id}`" 
            target="_blank"
          >
            <strong>{{ doc.titolo }}</strong>
          </a>
          <div class="doc-id">ID: {{ doc.id }}</div>
        </li>
      </ul>
      <div class="button-row">
        <button class="secondary" @click="closeOverlay">Chiudi</button>
        <button 
          v-if="currentOverlayTarget"
          @click="focusFromOverlay"
        >
          Continua con <strong>{{ currentOverlayTarget && currentOverlayTarget.id }}</strong>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";

export default {
  name: "MoroNetworkChart",
  
  data() {
    return {
      focusedNode: null,
      currentOverlayTarget: null,
      showOverlay: false,
      overlayTitle: '',
      overlaySub: '',
      overlayDocs: [],
      tooltipStyle: {
        opacity: 0,
        left: '0px',
        top: '0px'
      },
      svg: null,
      nodes: [],
      linksByNode: new Map(),
      nodeCircles: null,
      labelG: null,
      hoverLabelG: null,
      color: null
    };
  },

  async mounted() {
    await this.initChart();
  },

  methods: {
    async initChart() {
      const API_ENDPOINT = '/api/moro-network';
      const width = 950;
      const height = 950;

      this.svg = d3.create("svg")
        .attr("viewBox", `${-width/2} ${-height/2} ${width} ${height}`)
        .attr("width", width)
        .attr("height", height);

      this.$refs.chart.appendChild(this.svg.node());

      try {
        const response = await fetch(API_ENDPOINT);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const network = await response.json();
        this.initNetwork(network, width, height);
      } catch (err) {
        console.error(err);
        alert('Errore caricamento dati: ' + err.message);
      }
    },

    initNetwork(network, width, height) {
      const nodesData = network.nodes.map(d => Object.assign({}, d));
      const linksData = network.links.map(d => Object.assign({}, d));

      const root = d3.hierarchy({children: nodesData})
        .sum(d => d.value || 1)
        .sort((a,b) => (b.value - a.value));

      const pack = d3.pack().size([width*0.85, height*0.85]).padding(2);
      const packed = pack(root);

      this.nodes = packed.leaves().map(l => ({
        id: l.data.id,
        uri: l.data.uri,
        value: l.data.value || 1,
        documents: l.data.documents || [],
        x: l.x - width/2 + width*0.075,
        y: l.y - height/2 + height*0.075,
        r: Math.max(3, l.r * 0.7),
        originalX: l.x - width/2 + width*0.075,
        originalY: l.y - height/2 + height*0.075,
        originalR: Math.max(3, l.r * 0.7)
      }));

      linksData.forEach(l => {
        if (!this.linksByNode.has(l.source)) this.linksByNode.set(l.source, []);
        if (!this.linksByNode.has(l.target)) this.linksByNode.set(l.target, []);
        this.linksByNode.get(l.source).push({other: l.target, shared_docs: l.shared_docs, shared_count: l.shared_count});
        this.linksByNode.get(l.target).push({other: l.source, shared_docs: l.shared_docs, shared_count: l.shared_count});
      });

      const values = this.nodes.map(d => d.value);
      const maxValue = d3.max(values) || 1;
      const minValue = d3.min(values) || 1;
      
      const logScale = d3.scaleLog()
        .domain([Math.max(1, minValue), Math.max(2, maxValue)])
        .range([0, 1])
        .clamp(true);
      
      this.color = d3.scaleSequential()
        .domain([0, 1])
        .interpolator(d3.interpolateRgbBasis([
          'rgba(203, 213, 225, 1)',
          'rgba(148, 163, 184, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(37, 99, 235, 1)',
          'rgba(29, 78, 216, 1)',
          'rgba(30, 58, 138, 1)'
        ]));
      
      this.getColor = (value) => {
        const normalized = logScale(value);
        return this.color(normalized);
      };

      const nodeG = this.svg.append('g').attr('class', 'nodes');
      this.labelG = this.svg.append('g').attr('class', 'labels');

      this.nodeCircles = nodeG.selectAll('g.node')
        .data(this.nodes, d => d.id)
        .join('g')
          .attr('class', 'node')
          .attr('transform', d => `translate(${d.x},${d.y})`)
          .style('cursor', 'pointer')
          .on('mouseover', (event, d) => this.onNodeMouseOver(event, d))
          .on('mousemove', (event) => this.onNodeMouseMove(event))
          .on('mouseout', () => this.onNodeMouseOut())
          .on('click', (event, d) => this.onNodeClick(event, d));

      this.nodeCircles.append('circle')
        .attr('r', d => d.r)
        .attr('fill', d => this.getColor(d.value))
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);

      this.svg.on('click', (event) => {
        if (event.target === this.svg.node()) this.resetView();
      });
    },

    onNodeMouseOver(event, d) {
      d3.select(event.currentTarget).classed('highlight', true)
        .select('circle')
        .attr('stroke', '#1e40af')
        .attr('stroke-width', 2.5);
      
      if (!this.focusedNode) {
        this.tooltipStyle.opacity = 1;
        const tooltip = document.getElementById('tooltip');
        tooltip.innerHTML = `<b>${d.id}</b><br>Menzioni: ${d.value}`;
        this.tooltipStyle.left = (event.pageX + 10) + 'px';
        this.tooltipStyle.top = (event.pageY - 20) + 'px';
      }
    },

    onNodeMouseMove(event) {
      this.tooltipStyle.left = (event.pageX + 10) + 'px';
      this.tooltipStyle.top = (event.pageY - 20) + 'px';
    },

    onNodeMouseOut() {
      d3.selectAll('.node').classed('highlight', false)
        .select('circle')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);
      this.tooltipStyle.opacity = 0;
    },

    onNodeClick(event, d) {
      event.stopPropagation();
      
      if (!this.focusedNode) {
        this.focusToNode(d);
      } else if (d.id === this.focusedNode.id) {
        return;
      } else {
        const conn = (this.linksByNode.get(this.focusedNode.id) || []).find(c => c.other === d.id);
        if (conn) {
          this.showOverlayNode(d, conn);
        }
      }
    },

    focusToNode(centerNode) {
      this.focusedNode = centerNode;

      const connections = this.linksByNode.get(centerNode.id) || [];
      const connectedIds = new Set(connections.map(c => c.other));
      connectedIds.add(centerNode.id);

      const connectedNodes = this.nodes.filter(n => connectedIds.has(n.id));
      const petalNodes = connectedNodes.filter(n => n.id !== centerNode.id);

      const petalNodesWithData = petalNodes.map(n => {
        const conn = connections.find(c => c.other === n.id);
        return {node: n, shared_count: conn.shared_count || 0, value: n.value};
      }).sort((a, b) => b.shared_count - a.shared_count);

      const sharedCounts = petalNodesWithData.map(item => item.shared_count);
      const maxShared = d3.max(sharedCounts) || 1;
      const minShared = d3.min(sharedCounts) || 1;
      
      const logScale = d3.scaleLog()
        .domain([Math.max(1, minShared), Math.max(2, maxShared)])
        .range([0, 1])
        .clamp(true);
      
      const focusColorScale = d3.scaleSequential()
        .domain([0, 1])
        .interpolator(d3.interpolateRgbBasis([
          'rgba(203, 213, 225, 1)',
          'rgba(148, 163, 184, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(37, 99, 235, 1)',
          'rgba(29, 78, 216, 1)',
          'rgba(30, 58, 138, 1)'
        ]));

      const minRadius = 80;
      const maxRadius = 440;
      const totalNodes = petalNodesWithData.length;
      
      const turns = Math.max(2, Math.ceil(totalNodes / 10));
      const radiusIncrement = (maxRadius - minRadius) / Math.max(1, totalNodes - 1);
      const angleIncrement = (turns * 3 * Math.PI) / Math.max(1, totalNodes - 1);
      
      const sizeScale = d3.scaleLog()
        .domain([Math.max(1, minShared), Math.max(2, maxShared)])
        .range([6, 14])
        .clamp(true);
      
      petalNodesWithData.forEach((item, i) => {
        const node = item.node;
        
        const radius = minRadius + (i * radiusIncrement);
        const angle = (i * angleIncrement) - Math.PI / 2;
        
        node.focusX = radius * Math.cos(angle);
        node.focusY = radius * Math.sin(angle);
        node.focusR = sizeScale(item.shared_count);
        node.shared_count = item.shared_count;
        
        const normalizedValue = logScale(item.shared_count);
        node.focusColor = focusColorScale(normalizedValue);
      });

      centerNode.focusX = 0;
      centerNode.focusY = 0;
      centerNode.focusR = 15;
      centerNode.focusColor = this.getColor(centerNode.value);

      this.nodeCircles
        .transition()
        .duration(600)
        .style('opacity', d => connectedIds.has(d.id) ? 1 : 0)
        .attr('transform', d => {
          if (!connectedIds.has(d.id)) return `translate(${d.x},${d.y})`;
          return `translate(${d.focusX},${d.focusY})`;
        });

      this.nodeCircles.selectAll('circle')
        .transition()
        .duration(600)
        .attr('r', d => connectedIds.has(d.id) ? d.focusR : d.r)
        .attr('fill', d => connectedIds.has(d.id) ? d.focusColor : this.color(d.value));

      this.labelG.selectAll('text.label')
        .data([centerNode], d => d.id)
        .join(
          enter => enter.append('text')
            .attr('class', 'label')
            .attr('text-anchor', 'middle')
            .attr('dy', '.35em')
            .style('fill', '#111')
            .style('font-size', '16px')
            .style('font-weight', '600')
            .style('opacity', 0)
            .text(d => d.id.length > 30 ? d.id.substring(0, 30) + '...' : d.id)
            .attr('x', 0)
            .attr('y', 0)
            .call(enter => enter.transition().duration(600).delay(200).style('opacity', 1)),
          update => update.transition().duration(600)
            .attr('x', 0)
            .attr('y', 0)
            .text(d => d.id.length > 30 ? d.id.substring(0, 30) + '...' : d.id),
          exit => exit.transition().duration(400).style('opacity', 0).remove()
        );

      if (this.hoverLabelG) this.hoverLabelG.remove();
      this.hoverLabelG = this.svg.append('g').attr('class', 'hover-labels');
      
      const vm = this;
      
      this.nodeCircles
        .filter(d => connectedIds.has(d.id) && d.id !== centerNode.id)
        .on('mouseover.label', function(event, d) {
          if (!vm.focusedNode) return;
          
          d3.select(this).select('circle')
            .transition().duration(150)
            .attr('r', d.focusR * 1.8)
            .attr('stroke-width', 2.5);
          
          vm.hoverLabelG.selectAll('*').remove();
          
          const conn = (vm.linksByNode.get(vm.focusedNode.id) || []).find(c => c.other === d.id);
          const sharedCount = conn ? conn.shared_count : 0;
          
          const labelText = d.id.length > 25 ? d.id.substring(0, 25) + '...' : d.id;
          const fullText = `${labelText} [Doc. condivisi: ${sharedCount}]`;
          
          const label = vm.hoverLabelG.append('text')
            .attr('class', 'hover-label')
            .attr('text-anchor', 'middle')
            .attr('dy', d.focusR + 20)
            .style('fill', '#111')
            .style('font-size', '13px')
            .style('font-weight', '500')
            .style('opacity', 0)
            .style('pointer-events', 'none')
            .text(fullText)
            .attr('x', d.focusX)
            .attr('y', d.focusY);
          
          const bbox = label.node().getBBox();
          vm.hoverLabelG.insert('rect', 'text')
            .attr('x', bbox.x - 4)
            .attr('y', bbox.y - 2)
            .attr('width', bbox.width + 8)
            .attr('height', bbox.height + 4)
            .attr('fill', 'rgba(255,255,255,0.95)')
            .attr('rx', 4)
            .style('opacity', 0)
            .transition().duration(150).style('opacity', 1);
          
          label.transition().duration(150).style('opacity', 1);
        })
        .on('mouseout.label', function(event, d) {
          if (!vm.focusedNode) return;
          
          d3.select(this).select('circle')
            .transition().duration(150)
            .attr('r', d.focusR)
            .attr('stroke-width', 1.5);
          
          vm.hoverLabelG.selectAll('*').transition().duration(150)
            .style('opacity', 0)
            .remove();
        });
    },

    showOverlayNode(targetNode, connection) {
      this.currentOverlayTarget = targetNode;
      this.overlayTitle = `${this.focusedNode.id} ↔ ${targetNode.id}`;
      this.overlaySub = `Documenti condivisi: ${connection.shared_count}`;
      this.overlayDocs = connection.shared_docs || [];
      this.showOverlay = true;
    },

    closeOverlay() {
      this.showOverlay = false;
      this.currentOverlayTarget = null;
    },

    focusFromOverlay() {
      if (this.currentOverlayTarget) {
        this.showOverlay = false;
        this.focusToNode(this.currentOverlayTarget);
        this.currentOverlayTarget = null;
      }
    },

    resetView() {
      this.focusedNode = null;
      this.currentOverlayTarget = null;
      this.tooltipStyle.opacity = 0;
      this.showOverlay = false;

      this.nodeCircles.on('mouseover.label', null).on('mouseout.label', null);

      this.nodeCircles
        .transition()
        .duration(600)
        .style('opacity', 1)
        .attr('transform', d => `translate(${d.x},${d.y})`);

      this.nodeCircles.selectAll('circle')
        .transition()
        .duration(600)
        .attr('r', d => d.r)
        .attr('fill', d => this.getColor(d.value));

      this.labelG.selectAll('text.label')
        .transition()
        .duration(400)
        .style('opacity', 0)
        .remove();
      
      if (this.hoverLabelG) {
        this.hoverLabelG.remove();
        this.hoverLabelG = null;
      }
    }
  }
};
</script>

<style scoped>
.bubble-chart {
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
  background: #f8fafc;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

h1 { 
  font-size: 20px; 
  margin: 8px 0 4px 0; 
  color: #111;
}

.subtitle { 
  font-size: 14px; 
  color: #666; 
  margin: 0 0 12px 0; 
}

.chart-container { 
  width: 950px; 
  max-width: 95vw; 
  position: relative; 
}

:deep(svg) { 
  background: #fff; 
  border-radius: 12px; 
  box-shadow: 0 2px 8px rgba(0,0,0,0.08); 
  cursor: pointer; 
}

.controls {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 1;
}

.controls button {
  background: rgba(30, 64, 175, 0.95);
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s;
}

.controls button:hover { 
  background: #2563eb; 
  transform: translateY(-1px); 
}

.controls button.hidden { 
  display: none; 
}

.tooltip {
  position: absolute;
  background: rgba(255,255,255,0.95);
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  z-index: 20;
  transition: opacity 0.2s;
}

#overlay {
  position: fixed;
  top: 50%; 
  left: 50%; 
  transform: translate(-50%, -50%);
  background: rgba(255,255,255,0.98);
  border-radius: 12px; 
  box-shadow: 0 6px 24px rgba(0,0,0,0.2);
  width: 520px; 
  max-width: 95vw; 
  max-height: 72vh;
  flex-direction: column; 
  padding: 20px; 
  z-index: 40; 
  overflow: hidden;
}

#overlay h2 { 
  margin: 0 0 6px 0; 
  font-size: 18px; 
}

#overlay p { 
  margin: 0 0 8px 0; 
  color: #444; 
  font-size: 14px; 
}

#overlay ul { 
  list-style: none; 
  padding: 0; 
  margin: 8px 0; 
  overflow-y: auto; 
  max-height: 42vh; 
  border-top: 1px solid #eee; 
  border-bottom: 1px solid #eee; 
}

#overlay li { 
  padding: 8px 0; 
  border-bottom: 1px solid #f0f0f0; 
  font-size: 14px;
}

#overlay li:last-child {
  border-bottom: none;
}

#overlay a { 
  color: #1e40af; 
  font-weight: 600; 
  text-decoration: none;
  display: block;
  margin-bottom: 2px;
}

#overlay a:hover {
  color: #2563eb;
  text-decoration: underline;
}

.doc-id {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

#overlay .button-row { 
  display: flex; 
  gap: 8px; 
  margin-top: 12px; 
  justify-content: flex-end; 
}

#overlay button { 
  background: #1e40af; 
  color: #fff; 
  border: none; 
  padding: 8px 12px; 
  border-radius: 8px; 
  cursor: pointer; 
  font-size: 14px; 
}

#overlay button:hover { 
  background: #2563eb;
}

#overlay button.secondary { 
  background: #6b7280; 
}

#overlay button.secondary:hover { 
  background: #4b5563; 
}

:deep(.node) { 
  transition: all 0.3s; 
}

:deep(.node circle) { 
  transition: all 0.3s; 
}

:deep(.node.highlight circle) { 
  stroke: #1e40af; 
  stroke-width: 2.5; 
}

:deep(.node text) { 
  pointer-events: none; 
  user-select: none; 
}

/* Small screen message */
.small-screen-message {
  display: none;
  width: 100%;
  padding: 48px 24px;
  box-sizing: border-box;
}

.small-screen-inner {
  max-width: 340px;
  margin: 0 auto;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  padding: 32px 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.screen-icon {
  font-size: 40px;
  margin-bottom: 16px;
}

.msg-it {
  font-size: 15px;
  color: #333;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.msg-en {
  font-size: 13px;
  color: #888;
  margin: 0;
  line-height: 1.5;
  font-style: italic;
}

@media (max-width: 768px) {
  .small-screen-message {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
  }

  h1,
  .subtitle,
  .chart-container {
    display: none;
  }
}
</style>